/* ────────────────────────────────────────────────────────────
 * CRM Admin navigation — the single source of truth.
 *
 * It drives three things so they can never drift apart:
 *   1. the short "CRM Admin" block in the sidebar (one link per group,
 *      built in sidebarMenuConfig.js),
 *   2. the tab strip CrmPage renders under every page header
 *      (Enquiry > Call | Web | Whatsapp | Chat Bot ...), and
 *   3. the "+ Add" menu in the page header.
 *
 * Sub-screens used to be ~50 nested sidebar rows; the mobile app shows
 * them as tabs inside a screen, which is what the web now does too.
 *
 * `matches` lists the detail / create routes that live under a tab, so
 * that tab (and the sidebar item) stays highlighted on those pages and
 * CrmPage knows to show a back button there.
 * ──────────────────────────────────────────────────────────── */

export const CRM_NAV = [
  {
    key: 'calls',
    label: 'Recent Calls',
    icon: 'calls',
    tabs: [{ label: 'Recent Calls', to: '/RecentCalls' }],
  },
  {
    key: 'dashboard',
    label: 'CRM Dashboard',
    icon: 'dashboard',
    tabs: [{ label: 'Dashboard', to: '/CRMAdminDashboard' }],
  },
  {
    key: 'leads',
    label: 'Leads',
    icon: 'leads',
    tabs: [
      { label: 'Lead Home', to: '/CRMLeadHome', matches: ['/CRMLeadDetails', '/CRMLeadsByUser'] },
      { label: 'Lead Search', to: '/CRMLeadSearch', matches: ['/CRMLeadFilter'] },
    ],
  },
  {
    key: 'enquiry',
    label: 'Enquiry',
    icon: 'enquiry',
    tabs: [
      { label: 'Call', to: '/CRMEnquiryCall' },
      { label: 'Web', to: '/CRMEnquiryWeb', matches: ['/CRMEnquiryWebDetails'] },
      { label: 'Whatsapp', to: '/CRMEnquiryWhatsapp', matches: ['/CRMWhatsappChat'] },
      { label: 'Chat Bot', to: '/CRMEnquiryChatBot', matches: ['/CRMWebChat'] },
    ],
  },
  {
    key: 'prospect',
    label: 'Prospect',
    icon: 'prospect',
    tabs: [
      { label: 'Call', to: '/CRMProspectCall' },
      { label: 'Web', to: '/CRMProspectWeb', matches: ['/CRMProspectDetails'] },
      { label: 'Whatsapp', to: '/CRMProspectWhatsapp' },
      { label: 'Chat Bot', to: '/CRMProspectChatBot', matches: ['/CRMProspectBotDetails'] },
      { label: 'Registered', to: '/CRMProspectRegistered' },
    ],
  },
  {
    key: 'customer',
    label: 'Customer',
    icon: 'customer',
    tabs: [
      { label: 'Call', to: '/CRMCustomerCall' },
      { label: 'Web', to: '/CRMCustomerWeb' },
      { label: 'Whatsapp', to: '/CRMCustomerWhatsapp' },
    ],
  },
  {
    key: 'outgoing',
    label: 'Outgoing - Sales',
    icon: 'outgoing',
    tabs: [
      { label: 'Bulk Call', to: '/CRMOutgoingBulkCall' },
      {
        label: 'Whatsapp Promotion',
        to: '/CRMOutgoingWhatsappPromotion',
        matches: ['/CRMOutgoingWhatsappPromotionCreate', '/CRMOutgoingPromotionDetails', '/CRMOutgoingCustomGroup'],
      },
      { label: 'SMS Promotion', to: '/CRMOutgoingSmsPromotion', matches: ['/CRMOutgoingSmsPromotionCreate'] },
      {
        label: 'Push Notification',
        to: '/CRMOutgoingPushNotification',
        matches: ['/CRMOutgoingPushNotificationCreate', '/CRMOutgoingPushNotificationDetails'],
      },
      { label: 'Push Groups', to: '/CRMOutgoingPushGroups' },
      { label: 'Stories & Updates', to: '/CRMOutgoingStories', matches: ['/CRMOutgoingStoryCreate'] },
      { label: 'Email Promotion', to: '/CRMOutgoingEmailPromotion' },
      { label: 'Offers & Deals', to: '/CRMOutgoingOffers', matches: ['/CRMOutgoingOfferCreate'] },
    ],
  },
  {
    key: 'reports',
    label: 'General Reports',
    icon: 'reports',
    tabs: [
      { label: 'Stats', to: '/CRMReportsStats' },
      { label: 'Activity Log', to: '/CRMReportsActivityLog' },
      { label: 'Call Report', to: '/CRMReportsCallReview', matches: ['/CRMReportsTotalCallReport'] },
      { label: 'Channel Report', to: '/CRMReportsChannelReport' },
      { label: 'Call Summary', to: '/CRMReportsCallSummary' },
      { label: 'Registered Users', to: '/CRMReportsRegisteredUsers' },
      { label: 'Whatsapp Report', to: '/CRMReportsWhatsapp' },
    ],
  },
  {
    key: 'contacts',
    label: 'Contacts',
    icon: 'contacts',
    tabs: [
      {
        label: 'Contact Management',
        to: '/CRMDrawerContactManagement',
        matches: ['/CRMContactDetails', '/CRMContactEdit', '/CRMContactProfile'],
      },
      { label: 'Contact Search', to: '/CRMDrawerContactSearch' },
      { label: 'Dialer', to: '/CRMDialer' },
    ],
  },
  {
    key: 'hr',
    label: 'HR',
    icon: 'hr',
    tabs: [
      { label: 'HR Dashboard', to: '/CRMHRDashboard' },
      { label: 'Candidate', to: '/CRMHRCandidate', matches: ['/CRMHRCandidateDetails'] },
      { label: 'Interested Candidate', to: '/CRMHRInterestedCandidate' },
      { label: 'Job Applicant', to: '/CRMHRJobApplicant' },
      { label: 'Whatsapp Promotion', to: '/CRMHRPromotionWhatsapp' },
      { label: 'SMS Promotion', to: '/CRMHRPromotionSms' },
    ],
  },
  {
    key: 'notifications',
    label: 'Notifications',
    icon: 'notifications',
    tabs: [
      { label: 'Overview', to: '/CRMNotifications' },
      { label: 'Leads', to: '/CRMNotificationsLeads' },
      { label: 'Live Call', to: '/CRMNotificationsLiveCall' },
      { label: 'Frequent Call', to: '/CRMNotificationsFrequentCall' },
      { label: 'Whatsapp', to: '/CRMNotificationsWhatsapp' },
      { label: 'Email Enquiry', to: '/CRMNotificationsEmailEnquiry' },
      { label: 'Call Review', to: '/CRMNotificationsCallReview' },
    ],
  },
  {
    key: 'add',
    label: 'Add Lead',
    icon: 'add',
    tabs: [
      { label: 'Add Lead', to: '/CRMAddLead' },
      { label: 'Add Lead (Hiring Team)', to: '/CRMAddLeadHiring' },
      { label: 'Add Prospect', to: '/CRMAddProspect' },
      { label: 'Job Applicant', to: '/CRMAddCandidate' },
      { label: 'Add Consultant', to: '/CRMAddConsultant' },
      { label: 'Add Supplier', to: '/CRMAddSupplier' },
    ],
  },
];

/** The six "create" forms — offered from the "+ Add" menu on every CRM page. */
export const ADD_MENU = CRM_NAV.find((g) => g.key === 'add').tabs;

const norm = (p) => (p && p.length > 1 ? p.replace(/\/+$/, '') : p || '');

/** Every route a group owns (tab routes plus their detail / create routes). */
export const groupPaths = (group) => group.tabs.flatMap((t) => [t.to, ...(t.matches || [])]);

/** Every CRM Admin route — used for the sidebar section's "active" state. */
export const ALL_CRM_PATHS = CRM_NAV.flatMap(groupPaths);

/**
 * Where a pathname sits: its group, the tab it belongs to, and whether it is
 * a detail / create page under that tab (so CrmPage can show a back button).
 */
export const resolveNav = (pathname) => {
  const p = norm(pathname);
  for (const group of CRM_NAV) {
    for (const tab of group.tabs) {
      if (norm(tab.to) === p) return { group, tab, isDetail: false };
      if ((tab.matches || []).some((m) => norm(m) === p)) return { group, tab, isDetail: true };
    }
  }
  return { group: null, tab: null, isDetail: false };
};
